/**
 * EventBus — game-wide event emitter, not tied to any scene.
 *
 * Stored in game.registry so all scenes can access the same bus.
 */

type Handler = (...args: any[]) => void;

export class EventBus {
  private listeners: Map<string, Handler[]> = new Map();

  on(event: string, handler: Handler): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(handler);
    return this;
  }

  off(event: string, handler: Handler): this {
    const handlers = this.listeners.get(event);
    if (handlers) {
      const idx = handlers.indexOf(handler);
      if (idx !== -1) handlers.splice(idx, 1);
    }
    return this;
  }

  once(event: string, handler: Handler): this {
    const wrapper: Handler = (...args) => {
      this.off(event, wrapper);
      handler(...args);
    };
    return this.on(event, wrapper);
  }

  emit(event: string, ...args: any[]): this {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const h of [...handlers]) {
        h(...args);
      }
    }
    return this;
  }
}
